import { type ListResearchReportsQuery, type ResearchReportListItem, useResearchReports } from "@app/queries/research";
import Icon from "@lib/common/components/Icon/Icon";
import TablePagination from "@lib/common/components/TablePagination/TablePagination";
import TimeAgo from "@lib/common/components/TimeAgo/TimeAgo";
import ModalContent from "@lib/common/components/modals/ModalContent";
import ModalFooter from "@lib/common/components/modals/ModalFooter";
import ModalHeader from "@lib/common/components/modals/ModalHeader";
import { useTableSortingPagination } from "@lib/common/hooks/useTablePagination";
import { Alert, Button, Group, Stack, Text, TextInput, Tooltip } from "@mantine/core";
import { useDebouncedCallback } from "@mantine/hooks";
import { differenceBy } from "lodash-es";
import { DataTable } from "mantine-datatable";
import { useMemo, useState } from "react";

const PAGE_SIZE = 10;

export type DraftReport = {
  id: string;
  title: string;
};

export default function AttachReportModalContent({
  onSelect,
  onClose,
  selectedIds
}: {
  onSelect: (reports: DraftReport[]) => void;
  onClose: () => void;
  selectedIds: Set<string>;
}) {
  const [nameFilter, setNameFilter] = useState("");
  const [query, setQuery] = useState<ListResearchReportsQuery>({
    limit: PAGE_SIZE,
    offset: 0,
    search: undefined
  });
  // Full draft records (not just ids) so Attach works for rows selected on other pages.
  const [checkedReports, setCheckedReports] = useState<DraftReport[]>([]);

  const { data, isFetching, isError, error } = useResearchReports(query);
  const pageRecords = data?.results ?? [];
  const totalCount = data?.count ?? 0;

  const { currentPage, setCurrentPage, totalPages } = useTableSortingPagination<ResearchReportListItem>({
    pageSize: PAGE_SIZE,
    initialSort: { columnAccessor: "created_at", direction: "desc" },
    onQueryChange: params => {
      setQuery(prev => ({ ...prev, limit: params.limit, offset: params.offset }));
    },
    totalCount
  });

  const debouncedSearch = useDebouncedCallback((value: string) => {
    setCurrentPage(1);
    setQuery(prev => ({ ...prev, offset: 0, search: value || undefined }));
  }, 300);

  const selectedOnPage = pageRecords.filter(r => selectedIds.has(r.id) || checkedReports.some(c => c.id === r.id));

  const columns = useMemo(
    () => [
      {
        accessor: "title",
        title: "Report",
        width: "100%",
        ellipsis: true,
        render: (item: ResearchReportListItem) => (
          <Group gap="xs" align="center" wrap="nowrap">
            <Icon name="file-lines" size="0.9rem" color="dimmed" />
            <Text>{item.title}</Text>
          </Group>
        )
      },
      {
        accessor: "created_at",
        title: "Created",
        ellipsis: true,
        render: ({ created_at }: ResearchReportListItem) => (
          <TimeAgo date={new Date(created_at)} c="dimmed" size="sm" style={{ whiteSpace: "nowrap" }} />
        )
      }
    ],
    []
  );

  const handleSelectedRecordsChange = (pageSelected: ResearchReportListItem[]) => {
    const selectablePage = pageRecords.filter(r => !selectedIds.has(r.id));
    setCheckedReports(prev => [
      ...differenceBy(prev, selectablePage, "id"),
      ...pageSelected.filter(r => !selectedIds.has(r.id))
    ]);
  };

  const toggleRecord = (record: ResearchReportListItem) => {
    if (selectedIds.has(record.id)) {
      return;
    }
    setCheckedReports(prev =>
      prev.some(r => r.id === record.id) ? prev.filter(r => r.id !== record.id) : [...prev, record]
    );
  };

  const handleClose = () => {
    onClose();
  };

  const handleAttach = () => {
    if (checkedReports.length === 0) {
      return;
    }
    onSelect(checkedReports);
  };

  return (
    <>
      <ModalHeader title="Attach report" onClose={handleClose} />
      <ModalContent>
        <Stack>
          <Group gap="xs" wrap="nowrap">
            <Text size="sm" c="dimmed">
              Attach research reports to send with your next message.
            </Text>
            <Tooltip label="Looking for something new? Ask the agent to research it for you in the chat.">
              <Icon name="circle-question" size="0.9rem" color="dimmed" />
            </Tooltip>
          </Group>

          {isError && (
            <Alert color="red" title="Failed to load reports">
              {error instanceof Error ? error.message : "Unknown error"}
            </Alert>
          )}

          <TextInput
            placeholder="Search by report title..."
            value={nameFilter}
            onChange={e => {
              setNameFilter(e.currentTarget.value);
              debouncedSearch(e.currentTarget.value);
            }}
            autoFocus
          />

          <DataTable<ResearchReportListItem>
            columns={columns}
            records={pageRecords}
            fetching={isFetching}
            minHeight={150}
            noRecordsIcon={<Icon name="file-lines" size="1.2rem" style={{ marginBlock: 10 }} />}
            noRecordsText="No reports found"
            selectedRecords={selectedOnPage}
            onSelectedRecordsChange={handleSelectedRecordsChange}
            isRecordSelectable={record => !selectedIds.has(record.id)}
            onRowClick={({ record }) => toggleRecord(record)}
            highlightOnHover
            style={{ cursor: "pointer" }}
          />

          <TablePagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} p="none" />
        </Stack>
      </ModalContent>
      <ModalFooter>
        <Button variant="light" color="gray" onClick={handleClose}>
          Cancel
        </Button>
        <Button onClick={handleAttach} disabled={checkedReports.length === 0}>
          Attach{checkedReports.length > 0 ? ` (${checkedReports.length})` : ""}
        </Button>
      </ModalFooter>
    </>
  );
}
